import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Loader2 } from 'lucide-react';
import EToast from '@/components/everridge/EToast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import {
    APP_AI_DEEPSEEK_DEFAULT_MODEL,
    APP_AI_OPENAI_DEFAULT_MODEL,
    APP_AI_PROVIDER_DEEPSEEK,
    APP_AI_PROVIDER_OPEN_AI,
    APP_AI_SECRET_PLACEHOLDER,
} from './constants/app-ai.constants';
import { useAppAiSettingsApi } from './hooks/useAppAiSettingsApi';
import { useAppAiSettingsUpdateMutation } from './hooks/useAppAiSettingsMutation';

type FormValues = {
    isEnabled: boolean;
    provider: number;
    chatModel: string;
    apiKey: string;
};

function defaultChatModelForProvider(provider: number): string {
    return provider === APP_AI_PROVIDER_DEEPSEEK
        ? APP_AI_DEEPSEEK_DEFAULT_MODEL
        : APP_AI_OPENAI_DEFAULT_MODEL;
}

function normalizeUiProvider(apiProvider: number): number {
    if (apiProvider === APP_AI_PROVIDER_DEEPSEEK) return APP_AI_PROVIDER_DEEPSEEK;
    return APP_AI_PROVIDER_OPEN_AI;
}

function mergeKeyField(value: string, hadStoredCredential: boolean): string {
    const trimmed = value.trim();
    if (trimmed === APP_AI_SECRET_PLACEHOLDER || (trimmed === '' && hadStoredCredential)) {
        return APP_AI_SECRET_PLACEHOLDER;
    }
    return trimmed;
}

const AppAiSettingsPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userRole = useUserRole();

    const detailQ = useAppAiSettingsApi();
    const updateMut = useAppAiSettingsUpdateMutation(() => {
        EToast({ variant: 'success', message: t('superAdminSettings.ai.toastSaved') });
    });

    const { register, handleSubmit, reset, setValue } = useForm<FormValues>({
        defaultValues: {
            isEnabled: false,
            provider: APP_AI_PROVIDER_OPEN_AI,
            chatModel: APP_AI_OPENAI_DEFAULT_MODEL,
            apiKey: '',
        },
    });

    const { onChange: onProviderFieldChange, ...providerRegister } = register('provider', {
        valueAsNumber: true,
    });

    useEffect(() => {
        const d = detailQ.data;
        if (!d) return;
        const provider = normalizeUiProvider(d.provider);
        reset({
            isEnabled: d.isEnabled,
            provider,
            chatModel: d.chatModel ?? defaultChatModelForProvider(provider),
            apiKey: d.hasApiKey ? APP_AI_SECRET_PLACEHOLDER : '',
        });
    }, [detailQ.data, detailQ.dataUpdatedAt, reset]);

    if (userRole !== Roles.superAdmin) return null;

    const onSubmit = handleSubmit((values) => {
        const stored = detailQ.data;

        updateMut.mutate(
            {
                isEnabled: values.isEnabled,
                provider: values.provider,
                chatModel: values.chatModel.trim() ? values.chatModel.trim() : null,
                apiKey: mergeKeyField(values.apiKey, !!stored?.hasApiKey),
            },
            {
                onError: () =>
                    EToast({ variant: 'error', message: t('superAdminSettings.ai.toastFail') }),
            },
        );
    });

    const loading = detailQ.isLoading;
    const suffix =
        detailQ.data?.hasApiKey && detailQ.data?.apiKeyLast4 ? detailQ.data.apiKeyLast4 : null;

    return (
        <section className="flex h-full min-h-0 flex-col overflow-auto p-6">
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => navigate('/superAdmin/settings/general')}
                >
                    <ChevronLeft className="size-4" />
                </Button>
                <h1 className="text-xl font-medium text-black">{t('superAdminSettings.ai.pageTitle')}</h1>
            </div>

            <p className="mx-auto mb-6 w-full max-w-lg text-sm text-gray">
                {t('superAdminSettings.ai.subtitle')}
            </p>

            {loading ? (
                <div className="flex min-h-40 items-center justify-center">
                    <Loader2 className="size-8 animate-spin text-primary-color-1000" />
                </div>
            ) : detailQ.error ? (
                <p className="text-sm text-destructive">{t('superAdminSettings.ai.toastFail')}</p>
            ) : (
                <form className="mx-auto w-full max-w-lg space-y-5" onSubmit={onSubmit}>
                    <div className="flex items-center gap-2">
                        <input type="checkbox" id="ai-enabled" className="size-4" {...register('isEnabled')} />
                        <Label htmlFor="ai-enabled" className="font-normal">
                            {t('superAdminSettings.ai.enabled')}
                        </Label>
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="ai-provider">{t('superAdminSettings.ai.provider')}</Label>
                        <select
                            id="ai-provider"
                            className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            {...providerRegister}
                            onChange={(e) => {
                                onProviderFieldChange(e);
                                const v = Number(e.target.value);
                                setValue('chatModel', defaultChatModelForProvider(v));
                            }}
                        >
                            <option value={APP_AI_PROVIDER_OPEN_AI}>
                                {t('superAdminSettings.ai.providerOpenAi')}
                            </option>
                            <option value={APP_AI_PROVIDER_DEEPSEEK}>
                                {t('superAdminSettings.ai.providerDeepSeek')}
                            </option>
                        </select>
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="ai-model">{t('superAdminSettings.ai.chatModel')}</Label>
                        <Input id="ai-model" {...register('chatModel')} autoComplete="off" />
                        <p className="text-xs text-gray">{t('superAdminSettings.ai.chatModelHelp')}</p>
                    </div>

                    <div className="space-y-1">
                        <Label htmlFor="ai-key">{t('superAdminSettings.ai.apiKey')}</Label>
                        <Input id="ai-key" type="password" {...register('apiKey')} autoComplete="off" />
                        <p className="text-xs text-gray">{t('superAdminSettings.ai.keyUnchangedHint')}</p>
                        {suffix ? (
                            <p className="text-xs text-gray">
                                {t('superAdminSettings.ai.keySuffixHint', { suffix })}
                            </p>
                        ) : null}
                    </div>

                    <Button type="submit" disabled={updateMut.isPending}>
                        {t('superAdminSettings.ai.save')}
                    </Button>
                </form>
            )}
        </section>
    );
};

export default AppAiSettingsPage;
